import ajaxPromises from '../../../common/js/AjaxPromises';

const SCRIPT_CONTENT = `
// Listen clicks events
document.addEventListener('click', () => {
  window.parent.postMessage('clickFromIframe', '*');
});
// Listen on Exit tags
// Exit tags prevent the click event to be listen with the previous listener, so we should listen on the window.open event
// Here we can have access to the url, but for preview url we generally use the click command.
// We can probably add a custom param in order to allow the AdOps to use the url contains in the html and not the click command from the admin
window.open = function(url) {
  window.parent.postMessage('clickFromIframe', '*');
}
`;

const BLUR_EFFECT_STYLE = `
html, body, body > div {
  background-color: transparent !important;
}
`;

interface FetchCreativeOptions {
  shouldAddBlurEffectStyle: boolean;
}

export async function fetchCreative(
  url: string,
  options?: FetchCreativeOptions
): Promise<string> {
  const html = await fetchHtml(url);
  const wrappedHtml = new WrappedHtml(html)
    .addBaseToHead(url)
    .addScriptToBody();
  if (options?.shouldAddBlurEffectStyle) wrappedHtml.enableBlurEffect();
  return wrappedHtml.content;
}

function fetchHtml(url: string): Promise<string> {
  // Try to bypass the browser cache to avoid issue downloading the file
  // Cloudfront headers was not set as expected and leads to issues
  let finalUrl = url;
  const isBase64 = url.indexOf('base64') >= 0;
  if (!isBase64) {
    const new_url = new URL(url);
    new_url.searchParams.set('formats', 'true');
    finalUrl = new_url.toString();
  }

  return ajaxPromises.get(finalUrl, { timeout: 20000 }).catch(err => {
    throw new Error(`Failed to fetch creative url : ${JSON.stringify(err)}`);
  });
}

class WrappedHtml {
  private document: Document | null;
  private body: HTMLBodyElement | null;
  private head: HTMLHeadElement | null;

  get content(): string {
    return this.document!.documentElement.innerHTML;
  }

  constructor(html: string) {
    try {
      this.document = new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      throw new Error(`Failed to parse creative html : ${JSON.stringify(err)}`);
    }

    this.body = this.document.querySelector('body');
    if (this.body == null) throw new Error('Failed to find a body tag');

    this.head = this.document.querySelector('head');
    if (this.head == null) throw new Error('Failed to find a head tag');
  }

  addBaseToHead(url: string): WrappedHtml {
    // Set the base url for all relative links
    const base = document.createElement('base');
    const baseHref = url.substring(0, url.lastIndexOf('/') + 1);
    base.href = baseHref;

    // Important to put the base before all others children
    if (this.head?.firstChild) {
      this.head?.insertBefore(base, this.head?.firstChild);
    } else {
      this.head?.appendChild(base);
    }

    return this;
  }

  addScriptToBody(): WrappedHtml {
    const script = document.createElement('script') as HTMLScriptElement;
    script.innerHTML = SCRIPT_CONTENT;
    this.body?.appendChild(script);
    return this;
  }

  enableBlurEffect(): WrappedHtml {
    const blurStyles = document.createElement('style') as HTMLStyleElement;
    blurStyles.innerHTML = BLUR_EFFECT_STYLE;
    this.head?.appendChild(blurStyles);
    return this;
  }
}
